import { useLocation, useNavigate } from 'react-router-dom'
import {
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField
} from '@mui/material'
import { TitleHeader } from 'components/shop-reception/mobile/Common/TitleHeader'
import { Button, Divider, IconButton, DatePicker, Input } from '@fullscreendigital/carrefour-dex-fe'
import { useMutation, useQuery } from 'react-query'
import { ManageSearch as ManageSearchIcon } from '@mui/icons-material'
import { formatErrorMessage } from 'utils/formatting'
import { useNotification } from 'hooks'
import { useFormik } from 'formik'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import React, { useEffect, useMemo, useState } from 'react'
import AlertModal from '../../../../../../Common/AlertModal/AlertModal'
import { DefectReason, Mark } from '../../../../../../../enums'
import { URLS } from '../../../../../../constants'
import { validateScanCode } from '../../../../../../helpers'
import { useOfflineElementTool } from 'components/core-ui/Offline'
import { getInitialValues, dlcIsSame, normalizeDlc, makeArrayScanAwareDecimalChange } from "./helpers";
import { ScrollTopButton } from 'components/core-ui/ScrollTopButton'
import ImageUpload from 'components/shop-reception/mobile/Common/ImageUpload/ImageUpload'
import dayjs from 'dayjs'

interface ISupportSensitiveListModel {
  supportId?: number
  supportCode?: string
  supportNumber?: string
  productList?: Array<IProductModel>
}

interface IProductModel {
  id?: number
  name?: string
  ean?: string
  unit?: string
  quantity?: number
  integral?: number
  broken?: number
  shortDlc?: boolean
  dlc?: Date | string
  defectReason?: DefectReason
  mark?: Mark
  shopReceptionDeviation?: number
  supportId?: number
  eanReceived?: string
  code?: string
}

const DeliverySensitiveProductsConfirm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()

  const markOptions = useMemo(() =>
    Object.keys(Mark).map(key => ({ value: key, label: Mark[key] })), []
  )
  const defectReasonOptions = useMemo(() =>
      Object.keys(DefectReason).map(key => ({
        value: key,
        label: DefectReason[key]
      })),
    []
  )

  const { truckId, support } = location.state
  const [productCode, setProductCode] = useState('')
  const [shouldSearch, setShouldSearch] = useState(false)
  const [litigation, setLitigation] = useState([])
  const [uploadedImages, setUploadedImages] = useState({})
  const [currentIndex, setCurrentIndex] = useState<number | null>(null)
  const [integral, setIntegral] = useState<number | null>(null)
  const [broken, setBroken] = useState<number | null>(null)

  const [isSgrMessageModalOpen, setIsSgrMessageModalOpen] = useState(false)
  const [sgrMessageModalContent, setSgrMessageModalContent] = useState({ title: '', buttons: [] })
  const [isMandatoryDlcModalOpen, setIsMandatoryDlcModalOpen] = useState(false)
  const [mandatoryDlcModalContent, setMandatoryDlcModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isWrongQuantitiesModalOpen, setIsWrongQuantitiesModalOpen] = useState(false)
  const [wrongQuantitiesModalContent, setWrongQuantitiesModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isWrongDlcModalOpen, setIsWrongDlcModalOpen] = useState(false)
  const [wrongDlcModalContent, setWrongDlcModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isOkMessageModalOpen, setIsOkMessageModalOpen] = useState(false)
  const [okMessageModalContent, setOkMessageModalContent] = useState({ title: '', buttons: [] })
  const [isShortLifeMessageOpen, setIsShortLifeMessageOpen] = useState(false)
  const [shortLifeMessageContent, setShortLifeMessageContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [currentIndexes, setCurrentIndexes] = useState([])

  const { offlineData, deleteOfflineData, saveOfflineData } = useOfflineElementTool({
    dataType: 'DELIVERY_SENSITIVE_PRODUCTS_CONFIRM',
    url: URLS.AUTH_DELIVERY_SENSITIVE_PRODUCTS_CONFIRM,
    elementId: `${truckId}-${support.id}`
  })

  const { data: products, isLoading } = useQuery<ISupportSensitiveListModel>(
    ['sensitive-products', support.id],
    async () => {
      const result = await makeCoreGetRequest(
        `/shop-reception-mobile/sensitive/products/${support.id}`
      )

      return result || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: sensitiveProduct, isLoading: isLoadingSensitiveProduct } = useQuery(
    ['sensitive-product-scan'],
    async () => {
      if (!validateScanCode(showNotification, productCode, 'produs')) {
        return []
      }

      const result = await makeCoreGetRequest(
        `/shop-reception-mobile/sensitive/product/scan/${support.id}/${productCode}`,
        null
      )

      if (result.found) {
        setProductCode('')

        const inputElement = document.getElementById(result.id?.toString())
        if (inputElement) {
          inputElement.focus()
        }

        if (result.sgr) {
          showSgrMessage()
        }

        if (result.eanReceived !== null) {
          const idx = formik.values.productList.findIndex(product => product?.id === result?.id)
          if (idx !== -1) {
            formik.setFieldValue(`productList[${idx}].eanReceived`, result.eanReceived)
          }
        }
      } else {
        let message = 'Produsul nu se află in lista de mai jos.'
        if (!result.inMeti) {
          message = 'Produsul inexistent!'
        }

        showMandatoryDlcMessage(message)
      }

      return result || []
    },
    {
      enabled: shouldSearch,
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      onSettled: () => {
        setShouldSearch(false)
      }
    }
  )

  const saveProduct = async (values: any) => {
    return await makeCorePostRequest(
      `/shop-reception-mobile/sensitive/save/${truckId}/${support.id}`,
      values
    )
  }

  const { mutate } = useMutation(saveProduct, {
    onSuccess: async data => {
      if (data.shortLifeTime === true) {
        showShortLifeMessage(data.productList)
      } else {
        showNotification({
          message: `Produsul a fost salvat cu success.`,
          type: 'success'
        })
        await deleteOfflineData()
        support.shortDlcProducts
          ? navigate(URLS.AUTH_DELIVERY_SHORT_DLC_PRODUCTS, { state: { truckId, support } })
          : navigate(URLS.AUTH_DELIVERY_DETAIL, { state: truckId })
      }
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(
    () => getInitialValues(products, offlineData?.data),
    [products, offlineData?.data]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues ?? { productList: [] },
    onSubmit: async (values: any) => {
      if (hasEmptyDlc()) {
        showMandatoryDlcMessage('Data limită de consum este obligatorie!')
        return
      }

      const wrongQuantityProducts = []
      const wrongDlcProducts = []

      values.productList.forEach(product => {
        const conformQuantity = parseFloat(product.integral) || 0
        const defectQuantity = parseFloat(product.broken) || 0
        const validatedQuantity = conformQuantity + defectQuantity

        const dbProd = products.productList.find(dbProduct => dbProduct.id === product.id)
        if (dbProd) {
          const shopReceptionDeviation =
            dbProd.shopReceptionDeviation === null ? 0 : dbProd.shopReceptionDeviation / 1000

          const minAcceptedQuantity = dbProd.quantity - shopReceptionDeviation
          const maxAcceptedQuantity = dbProd.quantity + shopReceptionDeviation

          if (
            !(minAcceptedQuantity <= validatedQuantity && validatedQuantity <= maxAcceptedQuantity)
          ) {
            wrongQuantityProducts.push(`${dbProd.name} (${dbProd.code})`)
          }

          if (dbProd.dlc && dbProd.shortDlc === true && conformQuantity > 0) {
            if (!dlcIsSame(product.dlc, dbProd.dlc)) {
              wrongDlcProducts.push(`${dbProd.name} (${dbProd.code})`)
            }
          }
        }
      })

      let dlcMessage = ''
      if (wrongDlcProducts.length > 0) {
        wrongDlcProducts.forEach(mess => {
          dlcMessage += `${mess}`
        })
      }

      if (wrongQuantityProducts.length > 0) {
        let message = ''
        wrongQuantityProducts.forEach(mess => {
          message += `${mess}`
        })
        showWrongQuantitiesModal(message, values, dlcMessage)
      } else if (wrongDlcProducts.length > 0) {
        showWrongDlcModal(dlcMessage, values)
      } else {
        showOkMessage(values)
      }
    }
  })

  const proceedWithSubmission = values => {
    const payload = values.productList?.map(product => {
      const mark = markOptions.find(option => option.label === product.mark)
      const defectReason = defectReasonOptions.find(option => option.label === product.defectReason)

      const formattedDlc = normalizeDlc(product.dlc)
        ? normalizeDlc(product.dlc)!.format('YYYY-MM-DD')
        : null

      return {
        ...product,
        mark: mark?.value,
        defectReason: defectReason?.value,
        dlc: formattedDlc
      }
    })

    mutate(payload)
  }

  useEffect(() => {
    if (formik.values.productList[currentIndex]) {
      const newIndexes = [...new Set([...currentIndexes, currentIndex])]
      setCurrentIndexes(newIndexes)
      saveOfflineData([
        { field: `offlineLitigation`, value: litigation },
        {
          field: `offlineProductList[${currentIndex}].integral`,
          value: +formik.values.productList[currentIndex].integral
        },
        { field: 'offlineIntegral', value: +formik.values.productList[currentIndex].integral },
        { field: 'offlineCurrentIndex', value: currentIndex },
        { field: 'offlineCurrentIndexes', value: newIndexes },
        {
          field: `offlineProductList[${currentIndex}].broken`,
          value: +formik.values.productList[currentIndex].broken
        },
        { field: 'offlineBroken', value: +formik.values.productList[currentIndex].broken },
        { field: `offlineProductList[${currentIndex}].mark`, value: formik.values.productList[currentIndex].mark },
        { field: `offlineProductList[${currentIndex}].defectReason`,value: formik.values.productList[currentIndex].defectReason }
      ])
    }
  }, [litigation])

  const updateLitigation = (index, value) => {
    setLitigation(prevLitigation => {
      const newLitigation = [...prevLitigation]
      newLitigation[index] = value
      return newLitigation
    })
  }

  useEffect(() => {
    if (currentIndex !== null) {
      calculateLitigationType(currentIndex)
    }
  }, [integral, broken])

  useEffect(() => {
    if (currentIndex !== null) {
      calculateLitigationType(currentIndex, false)
    }
  }, [currentIndex])

  useEffect(() => {
    if (formik.values.productList.length) {
      formik.values.productList.forEach((_, idx) => {
        calculateLitigationType(idx, false)
      })
    }
  }, [products])

  useEffect(() => {
    if (offlineData) {
      if (!offlineData?.data || typeof offlineData.data !== 'object') {
        return;
      }

      const { offlineLitigation, offlineCurrentIndexes, offlineCurrentIndex } = offlineData.data
      // set litigation from offline data
      setLitigation(offlineLitigation || litigation || [])

      // set current indexes
      setCurrentIndexes(offlineCurrentIndexes || [])

      // set marks & reasons
      if (formik.values?.productList?.length) {
        offlineCurrentIndexes?.forEach(idx => {
          const savedInt = offlineData.data[`offlineProductList[${idx}].integral`];
          const savedBrk = offlineData.data[`offlineProductList[${idx}].broken`];

          calculateLitigationType(idx, false, savedInt, savedBrk)
          populateDefectReasons(idx)
        })

        // focus next element
        if (offlineCurrentIndex !== null) {
          const nextId = formik.values?.productList[offlineCurrentIndex + 1]?.id
          if (nextId) {
            const nextElement = document.getElementById(nextId.toString())
            if (nextElement) {
              nextElement.focus()
            }
          }
        }
      }
    }
  }, [offlineData, formik.values?.productList?.length])

  useEffect(() => {
    if (currentIndex !== null) {
      populateDefectReasons(currentIndex)
    }
  }, [formik.values.productList[currentIndex]?.mark, currentIndex])

  const calculateLitigationType = (
    idx,
    resetFieldValues = true,
    conformOverride?: number,
    defectOverride?: number
  ) => {
    const currentConformValue = conformOverride ?? parseFloat(formik.values.productList[idx].integral)
    const currentDefectValue = defectOverride ?? parseFloat(formik.values.productList[idx].broken)
    const currentTotalValue = currentConformValue + currentDefectValue
    const quantity = formik.values.productList[idx]?.quantity
    const shopReceptionDeviation =
      formik.values.productList[idx]?.shopReceptionDeviation === null
        ? 0
        : formik.values.productList[idx]?.shopReceptionDeviation / 1000

    let newMarks = []

    if (quantity > currentConformValue + shopReceptionDeviation && quantity === currentTotalValue) {
      newMarks = [
        Mark.VEGETABLE_FRUIT_QUALITY,
        Mark.EXPIRED,
        Mark.SHORT_TERM,
        Mark.DAMAGED,
        Mark.VEGETABLES_FRUITS,
        Mark.DETERIORATION_A
      ]

      updateLitigation(idx, 2)
    } else if (
      quantity > currentConformValue + shopReceptionDeviation &&
      quantity !== currentTotalValue
    ) {
      newMarks = [
        Mark.PALLETS_MERCHANDISE_MISSING,
        Mark.MISSING_CARRIER,
        Mark.QUANTITATIVE_DIFFERENCE
      ]

      updateLitigation(idx, 1)
    } else if (quantity < currentConformValue - shopReceptionDeviation) {
      newMarks = [Mark.PALLETS_MERCHANDISE_MISSING, Mark.QUANTITATIVE_DIFFERENCE]

      updateLitigation(idx, 1)
    } else if (
      quantity - shopReceptionDeviation <= currentConformValue &&
      currentConformValue <= quantity + shopReceptionDeviation
    ) {
      updateLitigation(idx, 0)
    }

    formik.setFieldValue(`productList[${idx}].marks`, newMarks)
    if (resetFieldValues) {
      formik.setFieldValue(`productList[${idx}].mark`, '')
      formik.setFieldValue(`productList[${idx}].defectReason`, '')
    }
  }

  const populateDefectReasons = idx => {
    if (formik.values.productList[idx]?.mark) {
      const selectedMark = formik.values.productList[idx]?.mark
      let newDefectReasons = []
      formik.setFieldValue(`productList[${idx}].defectReason`, '')

      switch (litigation[idx]) {
        case 1:
          newDefectReasons = [DefectReason.PROVIDER]
          formik.setFieldValue(`productList[${idx}].defectReason`, DefectReason.PROVIDER)
          saveOfflineData([
            {
              field: `offlineProductList[${idx}].mark`,
              value: formik.values.productList[idx].mark
            },
            {
              field: `offlineProductList[${idx}].defectReason`,
              value: DefectReason.PROVIDER
            }
          ])
          break
        case 2:
          if (selectedMark === Mark.EXPIRED || selectedMark === Mark.SHORT_TERM) {
            newDefectReasons = [DefectReason.PROVIDER]
            formik.setFieldValue(`productList[${idx}].defectReason`, DefectReason.PROVIDER)
            saveOfflineData([
              {
                field: `offlineProductList[${idx}].mark`,
                value: formik.values.productList[idx].mark
              },
              {
                field: `offlineProductList[${idx}].defectReason`,
                value: DefectReason.PROVIDER
              }
            ])
          } else if (selectedMark === Mark.VEGETABLE_FRUIT_QUALITY) {
            newDefectReasons = [DefectReason.PROVIDER, DefectReason.SHOP]
          } else if (selectedMark === Mark.DAMAGED) {
            newDefectReasons = [DefectReason.PROVIDER, DefectReason.TRANSPORTER, DefectReason.SHOP]
          } else if (
            selectedMark === Mark.VEGETABLES_FRUITS ||
            selectedMark === Mark.DETERIORATION_A
          ) {
            newDefectReasons = [DefectReason.PROVIDER, DefectReason.TRANSPORTER]
          }
          break
        default:
          newDefectReasons = []
      }

      formik.setFieldValue(`productList[${idx}].defectReasons`, newDefectReasons)
    }
  }

  const hasEmptyDlc = () => {
    let empty = false
    if (formik.values.productList) {
      formik.values.productList?.forEach(product => {
        const userDlc = product.dlc
        const integral = parseFloat(product.integral) || 0

        if (product.showDlc && !userDlc && integral > 0) {
          empty = true
        }
      })
    }
    return empty
  }

  const showSgrMessage = () => {
    setSgrMessageModalContent({
      title:
        'Acesta este un produs cu ambalaj returnabil - SGR! Va rugam sa gestionati aceste produs separat in vederea expunerii la raft.',
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsSgrMessageModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsSgrMessageModalOpen(true)
  }

  const closeSgrMessage = () => {
    setIsSgrMessageModalOpen(false)
  }

  const showMandatoryDlcMessage = message => {
    setMandatoryDlcModalContent({
      title: 'Atentie',
      description: message,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsMandatoryDlcModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input')?.focus()
            }, 0)
          },
          color: 'primary'
        }
      ]
    })
    setIsMandatoryDlcModalOpen(true)
  }

  const closeMandatoryDlcModal = () => {
    setIsMandatoryDlcModalOpen(false)
  }

  const showWrongQuantitiesModal = (message, formValues, dlcMessage) => {
    setWrongQuantitiesModalContent({
      title: 'Există diferenţe între cantitatea asteptată şi cea introdusă pentru produsele:',
      description: message,
      buttons: [
        {
          label: 'Mai verific',
          onClick: () => {
            setIsWrongQuantitiesModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input')?.focus()
            }, 0)
          },
          color: 'secondary'
        },
        {
          label: 'Am terminat',
          onClick: () => {
            setIsWrongQuantitiesModalOpen(false)
            if (dlcMessage.length > 0) {
              showWrongDlcModal(dlcMessage, formValues)
            } else {
              proceedWithSubmission(formValues)
            }
          },
          color: 'primary'
        }
      ]
    })
    setIsWrongQuantitiesModalOpen(true)
  }

  const closeWrongQuantitiesModal = () => {
    setIsWrongQuantitiesModalOpen(false)
  }

  const showWrongDlcModal = (message, formValues) => {
    setWrongDlcModalContent({
      title: 'Există diferenţe între DLC asteptat şi cel introdus pentru produsele:',
      description: message,
      buttons: [
        {
          label: 'Mai verific',
          onClick: () => {
            setIsWrongDlcModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input')?.focus()
            }, 0)
          },
          color: 'secondary'
        },
        {
          label: 'Am terminat',
          onClick: () => {
            setIsWrongDlcModalOpen(false)
            proceedWithSubmission(formValues)
          },
          color: 'primary'
        }
      ]
    })
    setIsWrongDlcModalOpen(true)
  }

  const closeWrongDlcModal = () => {
    setIsWrongDlcModalOpen(false)
  }

  const showOkMessage = formValues => {
    setOkMessageModalContent({
      title: 'Cantitaţile au fost înregistrate cu succes.',
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsOkMessageModalOpen(false)
            proceedWithSubmission(formValues)
          },
          color: 'primary'
        }
      ]
    })
    setIsOkMessageModalOpen(true)
  }

  const closeOkMessage = () => {
    setIsOkMessageModalOpen(false)
  }

  const showShortLifeMessage = productList => {
    let slMessage = ''
    if (productList.length > 0) {
      productList.forEach(mess => {
        slMessage = slMessage + mess
      })
    }

    setShortLifeMessageContent({
      title: 'Produsele următoare au valabilitate scurtă:',
      description: slMessage,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsShortLifeMessageOpen(false)
            support.shortDlcProducts
              ? navigate(URLS.AUTH_DELIVERY_SHORT_DLC_PRODUCTS, { state: { truckId, support } })
              : navigate(URLS.AUTH_DELIVERY_DETAIL, { state: truckId })
          },
          color: 'primary'
        }
      ]
    })
    setIsShortLifeMessageOpen(true)
  }

  const closeShortLifeMessage = () => {
    setIsShortLifeMessageOpen(false)
  }

  const onScan = React.useCallback((code: string) => {
    setProductCode(code)
    if (validateScanCode(showNotification, code, 'produs')) {
      setShouldSearch(true)
    }
    setTimeout(() => document.getElementById('scan-input')?.focus(), 0)
  }, [showNotification])

  const getArrayHandler = React.useMemo(() =>
    makeArrayScanAwareDecimalChange({
      setFieldValue: formik.setFieldValue,
      onScan,
      threshold: 5,
      afterChange: (idx, key, raw) => {
        if (key === 'integral') { setIntegral(+raw || 0); setCurrentIndex(idx) }
        if (key === 'broken')   { setBroken(+raw || 0);   setCurrentIndex(idx) }
      },
      getValue: (idx, key) => (formik.values.productList?.[idx] as any)?.[key]
    }), [formik.setFieldValue, onScan, formik.values.productList])

  const disabled = useMemo(() => formik.values.productList.some((product, idx) => {
    if (litigation[idx] === 2 && (uploadedImages[idx] !== true || !product.mark || !product.defectReason)) return true
    if (litigation[idx] === 1 && !product.mark) return true
    if (litigation[idx] !== 0 && !litigation[idx]) return true
    return false
  }), [formik.values.productList, litigation, uploadedImages])

  if (isLoading || isLoadingSensitiveProduct) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  return (
    <Grid container justifyContent={'center'} rowSpacing={2}>
      <Grid item xs={10}>
        <TitleHeader title="" />
      </Grid>

      <Grid item xs={10} mb={2}>
        <Paper elevation={3} style={{ padding: '20px' }} onClick={() => {}}>
          <Typography variant="medium" component="h5">
            PRODUSE SENSIBILE
          </Typography>
          <Typography variant="medium" component="p">
            Eticheta suport: {support.supportCode}
          </Typography>
          <Typography variant="medium" component="p">
            Numar suport: {support.supportNumber}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={10} style={{ display: 'flex' }}>
        <TextField
          type="search"
          variant="standard"
          label="Cod produs"
          fullWidth
          autoFocus
          id="scan-input"
          value={productCode}
          onChange={e => {
            const target = e.target as HTMLInputElement
            setProductCode(target.value)
          }}
          onKeyUp={event => {
            if (event.which == 0) {
              if (productCode && validateScanCode(showNotification, productCode, 'produs'))
                setShouldSearch(true)
            }
          }}
        />
        <IconButton
          onClick={() => {
            if (validateScanCode(showNotification, productCode, 'produs')) {
              setShouldSearch(true)
            }
          }}
        >
          <ManageSearchIcon />
        </IconButton>
      </Grid>

      <Grid item xs={10}>
        <Divider />
      </Grid>

      <Grid xs={10} item>
        <TableContainer>
          <Table>
            <TableHead style={{ backgroundColor: '#0970E6' }}>
              <TableRow>
                <TableCell style={{ color: 'white' }}>Produs</TableCell>
                <TableCell style={{ color: 'white' }}>Calitativ</TableCell>
                <TableCell style={{ color: 'white' }}>Defect</TableCell>
                <TableCell style={{ color: 'white' }}></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {formik.values?.productList?.map((product, index) => (
                <>
                  <TableRow>
                    <TableCell>{product.name}</TableCell>
                    <TableCell style={{ padding: "5px" }}>
                      <Input
                        id={product?.id?.toString()}
                        type="number"
                        variant="outlined"
                        fullWidth
                        value={product.integral}
                        onChange={getArrayHandler(index, 'integral')}
                      />
                    </TableCell>
                    <TableCell style={{ padding: "5px" }}>
                      <Input
                        type="number"
                        variant="outlined"
                        width="100%"
                        value={product.broken}
                        onChange={getArrayHandler(index, 'broken')}
                      />
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>

                  {litigation[index] === 2 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <ImageUpload
                          uploadUrl={`/shop-reception-mobile/integral/upload/${support.id}/${product.id}`}
                          onSuccess={() => {
                            setUploadedImages(prev => ({
                              ...prev,
                              [index]: true
                            }))

                            showNotification({
                              message: `Imaginea a fost incarcata cu succes.`,
                              type: 'success'
                            })
                          }}
                          onError={() =>
                            showNotification({
                              message: `A aparut o eroare la incarcarea imaginii.`,
                              type: 'error'
                            })
                          }
                        />
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell>
                      {product.ean} ({product.code}) ({product.unit})
                    </TableCell>
                    <TableCell style={{ padding: "5px" }}>
                      <Input
                        type="number"
                        variant="outlined"
                        width="100%"
                        value={product?.newIntegral}
                        onChange={event =>
                          formik.setFieldValue(
                            `productList[${index}].newIntegral`,
                            (event.target as HTMLInputElement).value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell style={{ padding: "5px" }}>
                      <Input
                        type="number"
                        variant="outlined"
                        width="100%"
                        value={product?.newBroken}
                        onChange={event =>
                          formik.setFieldValue(
                            `productList[${index}].newBroken`,
                            (event.target as HTMLInputElement).value
                          )
                        }
                      />
                    </TableCell>

                    <TableCell padding={'none'}>
                      <Button
                        variant="contained"
                        label="+"
                        onClick={async () => {
                          const newIntegral =
                            +formik.values.productList[index].integral +
                            (+formik.values.productList[index].newIntegral || 0)
                          const newBroken =
                            +formik.values.productList[index].broken +
                            (+formik.values.productList[index].newBroken || 0)

                          formik.setFieldValue(`productList[${index}].integral`, newIntegral)
                          formik.setFieldValue(`productList[${index}].broken`, newBroken)
                          formik.setFieldValue(`productList[${index}].newBroken`, 0)
                          formik.setFieldValue(`productList[${index}].newIntegral`, 0)

                          setIntegral(newIntegral)
                          setBroken(newBroken)
                          setCurrentIndex(index)
                          calculateLitigationType(index, true, newIntegral, newBroken)
                        }}
                      />
                    </TableCell>
                  </TableRow>

                  {!!litigation[index] && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Input
                          type="select"
                          label="Marca"
                          variant="outlined"
                          width="100%"
                          value={formik.values.productList[index].mark}
                          values={formik.values.productList[index].marks?.map(mark => ({
                            value: mark,
                            label: mark
                          }))}
                          onChange={async event => {
                            formik.setFieldValue(
                              `productList[${index}].mark`,
                              (event.target as HTMLInputElement).value
                            )
                            setCurrentIndex(index)

                            const newIndexes = [...new Set([...currentIndexes, index])]
                            setCurrentIndexes(newIndexes)

                            await saveOfflineData([
                              {
                                field: `offlineProductList[${index}].mark`,
                                value: (event.target as HTMLInputElement).value
                              },
                              { field: 'offlineCurrentIndex', value: index },
                              { field: 'offlineCurrentIndexes', value: newIndexes }
                            ])
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )}

                  {product.integral !== product.quantity && product.mark && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Input
                          type="select"
                          label="Motiv"
                          variant="outlined"
                          width="100%"
                          value={formik.values.productList[index].defectReason}
                          values={formik.values.productList[index].defectReasons?.map(defect => ({
                            value: defect,
                            label: defect
                          }))}
                          onChange={async event => {
                            formik.setFieldValue(
                              `productList[${index}].defectReason`,
                              (event.target as HTMLInputElement).value
                            )
                            await saveOfflineData([
                              {
                                field: `offlineProductList[${index}].defectReason`,
                                value: (event.target as HTMLInputElement).value
                              }
                            ])
                          }}
                          disabled={formik.values.productList[index].mark === ''}
                        />
                      </TableCell>
                    </TableRow>
                  )}

                  <TableCell colSpan={4}>
                    <DatePicker
                      onChange={async value => {
                        formik.setFieldValue(`productList[${index}].dlc`, value)
                        await saveOfflineData([
                          {
                            field: `offlineProductList[${index}].dlc`,
                            value: dayjs(value).format('YYYY-MM-DD')
                          }
                        ])
                      }}
                      value={product.dlc}
                      hidden={!product.showDlc}
                      variant="outlined"
                      style={{ display: 'flex', justifyContent: 'center' }}
                      adapterLocale="ro"
                      modalLabel="Selecteaza data"
                      closeButtonLabel="Anuleaza"
                      confirmButtonLabel="Confirma"
                    />
                  </TableCell>
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid xs={10} item>
        <Button
          variant="contained"
          label="Confirma cantitati"
          onClick={() => formik.handleSubmit()}
          style={{ width: '100%' }}
          disabled={disabled || formik.isSubmitting}
        />
      </Grid>

      <>
        <AlertModal
          open={isSgrMessageModalOpen}
          handleClose={closeSgrMessage}
          title={sgrMessageModalContent.title}
          buttons={sgrMessageModalContent.buttons}
        />
        <AlertModal
          open={isMandatoryDlcModalOpen}
          handleClose={closeMandatoryDlcModal}
          title={mandatoryDlcModalContent.title}
          description={mandatoryDlcModalContent.description}
          buttons={mandatoryDlcModalContent.buttons}
        />
        <AlertModal
          open={isWrongQuantitiesModalOpen}
          handleClose={closeWrongQuantitiesModal}
          title={wrongQuantitiesModalContent.title}
          description={wrongQuantitiesModalContent.description}
          buttons={wrongQuantitiesModalContent.buttons}
        />
        <AlertModal
          open={isWrongDlcModalOpen}
          handleClose={closeWrongDlcModal}
          title={wrongDlcModalContent.title}
          description={wrongDlcModalContent.description}
          buttons={wrongDlcModalContent.buttons}
        />
        <AlertModal
          open={isOkMessageModalOpen}
          handleClose={closeOkMessage}
          title={okMessageModalContent.title}
          buttons={okMessageModalContent.buttons}
        />
        <AlertModal
          open={isShortLifeMessageOpen}
          handleClose={closeShortLifeMessage}
          title={shortLifeMessageContent.title}
          description={shortLifeMessageContent.description}
          buttons={shortLifeMessageContent.buttons}
        />
      </>
      <ScrollTopButton />
    </Grid>
  )
}
export default DeliverySensitiveProductsConfirm
